/**
 * Immersive-world controls: back to worlds (top left) and a compact toolbar (bottom centre) with
 * light, weather, screenshot, fullscreen and settings. Controls auto-hide (handled by App).
 */
import { useRef, useState } from 'react';
import type { TimeMode } from '../framework/sim/clock';
import type { LightStage } from '../framework/sim/lightModel';
import type { WeatherMood } from '../framework/sim/weather';
import { QUALITY_LADDER } from '../framework/quality/profiles';
import type { AppController, AppSnapshot } from './controller';
import { Button, IconButton, Popover, Segmented } from './ui/controls';
import type { IconName } from './ui/icons';
import { Icon } from './ui/icons';

export const STAGE_LABEL: Record<LightStage, string> = {
  dawn: 'Dawn',
  morning: 'Morning',
  afternoon: 'Afternoon',
  golden: 'Golden hour',
  dusk: 'Dusk',
  night: 'Night',
};

const MODE_LABEL: Record<TimeMode, string> = { living: 'Living cycle', local: 'Local time', hold: 'Held' };
const MODE_HINT: Record<TimeMode, string> = {
  living: 'A full day passes in six hours, starting from your local time.',
  local: 'Follows the clock on this device.',
  hold: 'Keeps this light until you change it.',
};

const WEATHER: Record<WeatherMood, { label: string; icon: IconName; hint: string }> = {
  auto: { label: 'Auto', icon: 'auto', hint: 'The weather wanders gently on its own.' },
  clear: { label: 'Clear', icon: 'sun', hint: 'Settling toward clear skies.' },
  overcast: { label: 'Overcast', icon: 'cloud', hint: 'Soft, even light under cloud.' },
  rain: { label: 'Rain', icon: 'rain', hint: 'Cloud gathers, then rain arrives.' },
};

function clockText(hour: number): string {
  const h = Math.floor(hour) % 24;
  const m = Math.floor((hour - Math.floor(hour)) * 60);
  const d = new Date(2000, 0, 1, h, m);
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

export function fullscreenSupported(): boolean {
  const d = document as Document & { webkitFullscreenEnabled?: boolean };
  return !!(document.fullscreenEnabled || d.webkitFullscreenEnabled);
}

function toggleFullscreen(): void {
  const d = document as Document & { webkitFullscreenElement?: Element; webkitExitFullscreen?: () => void };
  const el = document.documentElement as HTMLElement & { webkitRequestFullscreen?: () => void };
  if (document.fullscreenElement || d.webkitFullscreenElement) {
    (document.exitFullscreen?.bind(document) ?? d.webkitExitFullscreen)?.();
  } else {
    (el.requestFullscreen?.bind(el) ?? el.webkitRequestFullscreen)?.();
  }
}

export function WorldChrome({ app, snap, onOpenSettings, onToast, isFullscreen }: { app: AppController; snap: AppSnapshot; onOpenSettings: () => void; onToast: (m: string) => void; isFullscreen: boolean }) {
  const [openPop, setOpenPop] = useState<null | 'time' | 'weather'>(null);
  const timeRef = useRef<HTMLButtonElement>(null);
  const weatherRef = useRef<HTMLButtonElement>(null);
  const st = snap.status;
  const mood = st?.weatherMood ?? 'auto';
  const [busy, setBusy] = useState(false);

  const shoot = async () => {
    if (busy) return;
    setBusy(true);
    const r = await app.screenshot();
    setBusy(false);
    if (r.ok) onToast(`Saved ${r.filename}`);
    else if (r.fallbackUrl) window.open(r.fallbackUrl, '_blank', 'noopener');
    else onToast(r.message);
  };

  return (
    <>
      {snap.frameStats && (
        <div className="gbr-fps" aria-hidden="true">
          {snap.frameStats.fps} fps · {snap.frameStats.width}×{snap.frameStats.height} · {QUALITY_LADDER[snap.qualityLevel]?.name}
        </div>
      )}
      <div className="gbr-topbar gbr-chrome">
        <Button variant="quiet" icon="back" onClick={() => app.exitToGallery()} aria-label="Back to worlds">
          Worlds
        </Button>
        <span className="gbr-topbar-title">{snap.manifest?.title}</span>
      </div>
      <nav className="gbr-toolbar gbr-surface gbr-chrome" aria-label="World controls">
        <button
          ref={timeRef}
          type="button"
          className="gbr-tool-label"
          aria-haspopup="dialog"
          aria-expanded={openPop === 'time'}
          aria-controls="gbr-pop-time"
          onClick={() => setOpenPop(openPop === 'time' ? null : 'time')}
        >
          <Icon name={st?.stage === 'night' || st?.stage === 'dusk' ? 'moon' : 'clock'} size={18} />
          <span>{st ? STAGE_LABEL[st.stage] : 'Light'}</span>
          {st && <small>{MODE_LABEL[st.timeMode]}</small>}
        </button>
        <IconButton buttonRef={weatherRef} icon={WEATHER[mood].icon} label={`Weather: ${WEATHER[mood].label}`} hasPopup="dialog" expanded={openPop === 'weather'} controls="gbr-pop-weather" onClick={() => setOpenPop(openPop === 'weather' ? null : 'weather')} />
        <span className="gbr-toolbar-sep" aria-hidden="true" />
        <IconButton icon="camera" label="Save a picture of this moment" onClick={() => void shoot()} disabled={busy} />
        {fullscreenSupported() && <IconButton icon={isFullscreen ? 'collapse' : 'expand'} label={isFullscreen ? 'Exit full screen' : 'Full screen'} onClick={toggleFullscreen} />}
        <IconButton icon="gear" label="Settings" hasPopup="dialog" onClick={onOpenSettings} />
      </nav>

      <Popover id="gbr-pop-time" label="Light and time" open={openPop === 'time'} onClose={() => setOpenPop(null)} anchor={timeRef}>
        <h3>Light</h3>
        <Segmented
          label="Time mode"
          value={st?.timeMode ?? 'living'}
          options={[
            { value: 'living', label: 'Living' },
            { value: 'local', label: 'Local time' },
            { value: 'hold', label: 'Hold' },
          ]}
          onChange={(v) => app.setTimeMode(v)}
        />
        <p className="gbr-hint" style={{ margin: '8px 0 14px' }}>
          {MODE_HINT[st?.timeMode ?? 'living']} {st && `Now ${clockText(st.hour)}.`}
        </p>
        <h3>Go to</h3>
        <div className="gbr-chips">
          {(['dawn', 'morning', 'afternoon', 'golden', 'dusk', 'night'] as LightStage[]).map((sg) => (
            <button key={sg} type="button" className="gbr-btn gbr-btn--chip" aria-pressed={st?.stage === sg} onClick={() => app.jumpToStage(sg)}>
              {STAGE_LABEL[sg]}
            </button>
          ))}
        </div>
      </Popover>

      <Popover id="gbr-pop-weather" label="Weather" open={openPop === 'weather'} onClose={() => setOpenPop(null)} anchor={weatherRef}>
        <h3>Weather</h3>
        <Segmented
          label="Weather mood"
          value={mood}
          options={(['auto', 'clear', 'overcast', 'rain'] as WeatherMood[]).map((w) => ({ value: w, label: WEATHER[w].label, icon: WEATHER[w].icon }))}
          onChange={(v) => app.setWeatherMood(v)}
        />
        <p className="gbr-hint" style={{ marginTop: 8 }}>
          {WEATHER[mood].hint} Changes arrive gradually.
        </p>
      </Popover>
    </>
  );
}
